<script lang="ts" setup>
import { useGetCompanySettings, type CompanySettings } from "@/api/companies";
import { LocationCategory } from "@/api/locationGroups";
import { useGetVisitorCategories } from "@/api/visitorCategories";
import {
  useCreateVisitor,
  type VisitorFieldValues,
  type VisitsRequest,
} from "@/api/visits";
import type { ExpandedUser } from "@/api/zoolander";
import CoreButton from "@/components/common/CoreButton/CoreButton.vue";
import { useLocationSelector } from "@/composables/location";
import { useLocationGroupsSettings } from "@/composables/locationGroups";
import { formatFullName } from "@/utils/string";
import {
  ElDivider,
  ElForm,
  ElFormItem,
  ElInput,
  ElOption,
  ElOptionGroup,
  ElSelect,
  type FormInstance,
  type FormItemInstance,
  type FormRules,
} from "element-plus";
import { computed, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import HostSelector from "./HostSelector.vue";
import VisitorFields from "./VisitorFields.vue";
import VisitorFormSkeleton from "./VisitorFormSkeleton.vue";
import type { Location } from "@/api/locations";
import { useValidation } from "@/composables/validation";

const emit = defineEmits<{
  (e: "created"): void;
}>();

const form = reactive({
  name: "",
  locationId: 0,
  categoryName: "",
  companyName: "",
  hostId: "",
  visitFactAt: "now",
});

const details = reactive({
  location: "",
  category: "",
  hostName: "",
  selectHost: false,
});

const fieldValues = reactive<VisitorFieldValues>({});
const { t, locale } = useI18n();
const { validateRange, required, validateFormItem } = useValidation();

const locationSearchQuery = ref("");
const { getLocationsByLocationGroups } = useLocationGroupsSettings();

const locationsByLocationGroups = computed(() =>
  getLocationsByLocationGroups(locationSearchQuery.value)
);
const createVisitorFormRef = ref<FormInstance>();
const nameRef = ref<FormItemInstance>();
const locationIdRef = ref<FormItemInstance>();
const categoryNameRef = ref<FormItemInstance>();
const companyNameRef = ref<FormItemInstance>();

const { mutate: createVisitorMutate, isLoading } = useCreateVisitor(() =>
  emit("created")
);

const {
  isAllLocationsSelected,
  locationGroups,
  selectedLocationGroup,
  selectedLocation,
  selectedLocationType,
} = useLocationSelector();

const locations = computed(() => {
  if (isAllLocationsSelected.value) {
    return locationGroups.value.flatMap(
      (locationGroup) => locationGroup.locations
    );
  }

  if (selectedLocationType.value === LocationCategory.GROUP) {
    return selectedLocationGroup.value?.locations;
  }

  return [selectedLocation.value];
});

const hasSelectedLocation = computed(() => Boolean(details.location));

const locationGroup = computed(() =>
  locationGroups.value.find((group) =>
    group.locations.map(({ id }) => id).includes(details.location)
  )
);

const locationGroupId = computed(() => locationGroup.value?.id ?? "");

const getCompanySettings = useGetCompanySettings({ locationGroupId });

const settings = computed<Partial<CompanySettings>>(
  () => getCompanySettings.data.value ?? {}
);

const getVisitorCategories = useGetVisitorCategories(locationGroup);
const categories = computed(
  () =>
    getVisitorCategories.data.value?.filter(
      (category) => category.settings.enabled
    ) ?? []
);

const selectedCategory = computed(() =>
  categories.value.find((category) => category.id === details.category)
);

const enabledInCategory = computed(() => {
  return !selectedCategory.value?.settings?.employee_selection;
});

const visitingEmployeeToggle = computed(() => {
  return settings.value?.visiting_employee_toggle;
});

const hasHostSelection = computed(() => {
  return Boolean(visitingEmployeeToggle.value && enabledInCategory.value);
});

const formLoading = computed(
  () =>
    getCompanySettings.isFetching.value || getVisitorCategories.isFetching.value
);

const companyNameState = computed(
  () => selectedCategory.value?.settings.company_field_state
);

const companyNameEnabled = computed(() =>
  ["required", "optional"].includes(companyNameState.value ?? "")
);

const companyNameRequired = computed(
  () => companyNameState.value === "required"
);

const companyNameRules = computed(() =>
  companyNameRequired.value ? [required(), validateRange(1, 128)] : []
);

const rules = computed<FormRules>(() => ({
  name: [required(), validateRange(1, 128)],
  locationId: [required()],
  categoryName: [required()],
  companyName: companyNameRules.value,
}));

watch(
  locations,
  (newLocations) => {
    details.location = newLocations?.[0]?.id ?? "";
    form.locationId = newLocations?.[0]?.numericId ?? 0;
  },
  { immediate: true }
);

watch(
  categories,
  (newCategories) => {
    if (newCategories.length === 1) {
      details.category = newCategories?.[0].id ?? "";
      return;
    }
    details.category = "";
    createVisitorFormRef.value?.clearValidate();
  },
  { immediate: true }
);

watch(
  selectedCategory,
  (newCategory) => {
    form.categoryName = newCategory?.name ?? "";
  },
  { immediate: true }
);

watch(
  () => details.category,
  () => {
    setTimeout(() => {
      createVisitorFormRef.value?.clearValidate();
    }, 0);
  },
  { immediate: true }
);

const handleLocationSearch = (value: string) => {
  locationSearchQuery.value = value;
};

const formatVisitorFields = () => {
  let formatVisitorFields = {};

  Object.entries(fieldValues).forEach(([fieldId, value]) => {
    formatVisitorFields = {
      ...formatVisitorFields,
      [`custom_${fieldId}`]: value,
    };
  });

  return formatVisitorFields;
};

const createVisitorPayload = (): VisitsRequest => {
  return {
    full_name: form.name,
    location_id: String(form.locationId),
    category_name: form.categoryName,
    visit_fact_at: form.visitFactAt,
    user_id: form.hostId || 0,
    company_name: form.companyName,
    ...formatVisitorFields(),
  };
};

const createVisitor = async () => {
  try {
    await createVisitorFormRef.value?.validate();
  } catch (err) {
    return;
  }
  const payload = createVisitorPayload();
  createVisitorMutate(payload);
};

const changeLocation = (locationId: string) => {
  const foundId = (locations.value as Array<Location>).find(
    (location) => location.id === locationId
  )?.numericId;
  if (typeof foundId === "undefined") {
    return;
  }
  form.locationId = foundId;
  clearHost();
};

const setHost = (host: ExpandedUser) => {
  form.hostId = host.id;
  details.hostName = formatFullName(host.firstName, host.lastName);
};

const clearHost = () => {
  form.hostId = "";
  details.hostName = "";
};

watch(locale, () => {
  const fieldRefs = {
    nameRef: nameRef.value,
    locationIdRef: locationIdRef.value,
    categoryNameRef: categoryNameRef.value,
    companyNameRef: companyNameRef.value,
  };

  validateFormItem(fieldRefs);
});
</script>

<template>
  <VisitorFormSkeleton
    :loading="formLoading"
    :has-title="false"
    :has-host="details.selectHost"
  >
    <el-form
      ref="createVisitorFormRef"
      label-position="top"
      :model="form"
      :rules="rules"
      scroll-to-error
      @submit.prevent="createVisitor"
    >
      <el-form-item
        ref="nameRef"
        :label="t('visitors.timeline.actions.fullName')"
        prop="name"
      >
        <el-input
          v-model="form.name"
          maxlength="128"
        />
      </el-form-item>

      <el-form-item
        ref="locationIdRef"
        :label="t('visitors.timeline.actions.location')"
        prop="locationId"
      >
        <el-select
          v-model="details.location"
          class="w-100 location-selection"
          value-key="pk"
          filterable
          remote
          :remote-method="handleLocationSearch"
          remote-show-suffix
          :teleported="false"
          fit-input-width
          @change="changeLocation"
        >
          <el-option-group
            v-for="locationGr in locationsByLocationGroups"
            :key="locationGr.name"
            :label="locationGr.name"
          >
            <el-option
              v-for="location in locationGr.locations"
              :key="location.id"
              :value="location.id"
              :label="location.name"
            />
          </el-option-group>
        </el-select>
      </el-form-item>

      <el-form-item
        ref="categoryNameRef"
        :label="t('visitors.timeline.actions.category')"
        prop="categoryName"
      >
        <el-select
          v-model="details.category"
          value-key="pk"
          :loading="getVisitorCategories.isLoading.value"
        >
          <el-option
            v-for="item in categories"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          />
        </el-select>
      </el-form-item>

      <HostSelector
        v-if="hasSelectedLocation && hasHostSelection"
        v-model="details.selectHost"
        :host-name="details.hostName"
        :location-id="details.location"
        @selected-host="setHost"
        @clear-host="clearHost"
      />

      <el-form-item
        v-if="companyNameEnabled"
        ref="companyNameRef"
        :label="settings.company_field_label"
        prop="companyName"
      >
        <el-input
          v-model="form.companyName"
          maxlength="128"
        />
      </el-form-item>

      <VisitorFields
        required
        :is-preregistration="false"
        :location-id="details.location"
        :category-id="details.category"
        @update="Object.assign(fieldValues, $event)"
      />

      <el-divider :class="{ 'no-divider': !details.category }" />

      <div class="submit">
        <CoreButton
          type="primary"
          class="add-button"
          :loading="isLoading"
          @click="createVisitor"
        >
          {{ t("visitors.timeline.actions.signIn") }}
        </CoreButton>
      </div>
    </el-form>
  </VisitorFormSkeleton>
</template>

<style lang="scss" scoped>
.so-select {
  width: 100%;
}

.location-selection :deep(.so-select-dropdown) {
  max-width: unset;
}

.submit {
  display: flex;
  justify-content: flex-end;
}

.add-button {
  min-width: 130px;

  @media screen and (max-width: $breakpoint-sm) {
    min-width: 110px;
  }
}

.no-divider {
  display: none;
}
</style>
